import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useCommonT } from '../../i18n'
import { ScanIcon } from './navIcons'
import { isAndroidApp } from '../../lib/androidApp'
import { useScannerLaunch } from '../../hooks/useScannerLaunch'

const ScanAppPanel = lazy(() => import('./ScanAppPanel'))

/**
 * The desktop header's purple Scan button (nav v2, plan W3). Opens a popover
 * under the header with a QR code to the iPhone app, the NavMenu / AuthButton
 * popover idiom: click outside or Escape closes, focus returns to the button,
 * and any navigation closes it. Hidden below md, where the tab bar's Scan
 * opens the Get the app sheet instead.
 */
const BUTTON_CLASSES = 'h-9 items-center gap-2 rounded-lg bg-purple-600 px-2.5 text-sm font-semibold text-white transition-colors hover:bg-purple-500 lg:px-3.5'

export default function ScanButton() {
  const t = useCommonT()
  const { pathname, search } = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const android = isAndroidApp()
  // The intent (targets + launch nonce) is built at click time.
  const scanner = useScannerLaunch(android)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false)
  }, [pathname, search])

  // Inside our Android app (a tablet is wide enough for this header button)
  // it opens the native scanner, not the QR to the iPhone app.
  if (android) {
    return (
      <a
        href={scanner.href}
        onClick={scanner.onClick}
        data-testid="header-scan-button"
        aria-label={t('nav.scan_cards')}
        className={`hidden md:flex ${BUTTON_CLASSES}`}
      >
        <ScanIcon className="w-5 h-5" />
        <span className="hidden lg:inline">{t('nav.scan')}</span>
      </a>
    )
  }

  return (
    <div ref={rootRef} className="relative hidden md:block">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="scan-app-popover"
        aria-label={t('nav.scan_cards')}
        className={`flex ${BUTTON_CLASSES}`}
      >
        <ScanIcon className="w-5 h-5" />
        <span className="hidden lg:inline">{t('nav.scan')}</span>
      </button>
      {open && (
        <div
          id="scan-app-popover"
          role="dialog"
          aria-label={t('scan.title')}
          className="absolute right-0 top-full mt-2 w-80 rounded-lg border border-gray-700 bg-gray-900 p-4 shadow-lg z-50"
        >
          <Suspense fallback={<div className="h-40" />}>
            <ScanAppPanel />
          </Suspense>
        </div>
      )}
    </div>
  )
}
